import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import Spinner from '../Atoms/Spinner';
import Section from '../Atoms/Section';
import H0 from '../Atoms/H0';
import GetProjectMainImage from '../Utils/GetProjectMainImage';
import { getEventDesignerDisplayName } from '../../Components/AdminPanels/utils/eventDesignerHelpers';
import { supabase } from '../../lib/supabaseClient';
import * as api from '../../api';

// ─── Date Gates ───────────────────────────────────────────────
const DATE_GATES = {
    PERSONALIZED_LINKS: new Date('2025-04-08T00:00:00'),
    CEREMONY_PHOTOS: new Date('2025-04-10T00:00:00'),
    CATALOGUE: new Date('2025-04-10T00:00:00'),
    DOSSIER_PRESSE_FINAL: new Date('2025-04-10T00:00:00'),
};

// ─── Styled Components ───────────────────────────────────────
const Container = styled.div`
    max-width: 1100px;
    margin: 0 auto;
    padding: 20px;
`;

const ConfidentialBanner = styled.div`
    background: #fff;
    color: #c0392b;
    text-align: center;
    padding: 14px 20px;
    font-weight: 700;
    font-size: 15px;
    margin-bottom: 30px;
    border: 2px solid #c0392b;
    border-radius: 6px;
`;

const KitHeader = styled.div`
    text-align: center;
    margin-bottom: 50px;
`;

const LogoImg = styled.img`
    max-width: 180px;
    max-height: 100px;
    object-fit: contain;
    margin-bottom: 16px;
`;

const Subtitle = styled.div`
    font-size: 20px;
    color: #555;
    margin-top: 8px;
`;

const partenaireColors = {
    fabricant: 'linear-gradient(135deg, #3498db, #2980b9)',
    ecole: 'linear-gradient(135deg, #9b59b6, #8e44ad)',
    distributeur: 'linear-gradient(135deg, #00897b, #00695c)',
    editeur: 'linear-gradient(135deg, #00897b, #00695c)',
    lieu: 'linear-gradient(135deg, #f9a825, #f57f17)',
    institution: 'linear-gradient(135deg, #e65100, #bf360c)',
    officiel: 'linear-gradient(135deg, #3f51b5, #283593)',
};

const PartenaireBadge = styled.div`
    background: ${props => partenaireColors[props.type] || 'linear-gradient(135deg, #666, #444)'};
    color: white;
    padding: 8px 20px;
    border-radius: 5px;
    font-weight: bold;
    font-size: 15px;
    display: inline-block;
    margin-top: 12px;
`;

// ─── Accordion ───────────────────────────────────────────────
const AccordionWrapper = styled.div`
    margin-bottom: 8px;
    border-bottom: 1px solid #ddd;
`;

const AccordionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 0;
    cursor: pointer;
    user-select: none;

    &:hover { opacity: 0.7; }
`;

const AccordionLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
`;

const SectionNumber = styled.span`
    font-size: 22px;
    font-weight: 700;
    color: rgb(24, 51, 189);
    min-width: 30px;
`;

const SectionTitle = styled.span`
    font-size: 20px;
    font-weight: 600;
    color: #222;

    @media (max-width: 768px) {
        font-size: 16px;
    }
`;

const ChevronIcon = styled.span`
    font-size: 20px;
    transition: transform 0.3s ease;
    transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const AccordionContent = styled.div`
    max-height: ${props => props.isOpen ? '10000px' : '0'};
    overflow: hidden;
    transition: max-height 0.4s ease, opacity 0.3s ease, padding 0.3s ease;
    opacity: ${props => props.isOpen ? 1 : 0};
    padding: ${props => props.isOpen ? '0 0 30px 0' : '0'};
`;

// ─── Subsections ─────────────────────────────────────────────
const SubsectionTitle = styled.h3`
    font-size: 17px;
    font-weight: 600;
    color: #333;
    margin: 28px 0 12px 0;
    padding-bottom: 6px;
    border-bottom: 1px solid #eee;

    &:first-child {
        margin-top: 10px;
    }
`;

const StaticTextBlock = styled.div`
    background: #f9f9f9;
    border-left: 3px solid rgb(24, 51, 189);
    padding: 16px 20px;
    border-radius: 0 6px 6px 0;
    color: #444;
    font-size: 15px;
    line-height: 1.6;
    margin-bottom: 16px;
`;

const ComingSoonPlaceholder = styled.div`
    background: #f8f9fa;
    border: 1px dashed #ccc;
    border-radius: 8px;
    padding: 24px;
    text-align: center;
    color: #999;
    font-style: italic;
    margin-bottom: 16px;

    .lock-icon { font-size: 28px; display: block; margin-bottom: 8px; }
`;

// ─── KV / Visuals ────────────────────────────────────────────
const KVProjectBlock = styled.div`
    margin-bottom: 36px;
    padding: 20px;
    background: #f8f9fa;
    border-radius: 8px;
`;

const KVRow = styled.div`
    display: flex;
    gap: 24px;
    flex-wrap: wrap;
    justify-content: center;
`;

const KVCard = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    max-width: 280px;

    img {
        width: 100%;
        height: auto;
        border: 1px solid #ddd;
        border-radius: 4px;
    }
`;

const DownloadBtn = styled.a`
    display: inline-block;
    margin-top: 10px;
    padding: 8px 16px;
    background: linear-gradient(135deg, #28a745, #20c997);
    color: white;
    text-decoration: none;
    border-radius: 5px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    border: none;
    transition: all 0.2s ease;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
        color: white;
        text-decoration: none;
    }

    &:visited, &:active, &:focus {
        color: white;
        text-decoration: none;
    }
`;

const VisualsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    margin-bottom: 16px;

    @media (max-width: 768px) {
        grid-template-columns: repeat(2, 1fr);
    }
`;

const VisualCard = styled.div`
    background: #f0f0f0;
    border: 1px solid #ddd;
    border-radius: 8px;
    padding: 20px;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 120px;

    .label {
        font-weight: 600;
        font-size: 14px;
        color: #555;
        margin-bottom: 8px;
    }

    .placeholder {
        color: #999;
        font-size: 12px;
        font-style: italic;
    }
`;

const LogosRow = styled.div`
    display: flex;
    gap: 20px;
    flex-wrap: wrap;
    margin-bottom: 16px;
`;

const VisualImageCard = styled.div`
    background: #f0f0f0;
    border: 1px solid #ddd;
    border-radius: 8px;
    overflow: hidden;
    text-align: center;
    display: flex;
    flex-direction: column;

    img {
        width: 100%;
        height: auto;
        display: block;
    }

    .card-footer {
        padding: 10px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
    }

    .card-label {
        font-weight: 600;
        font-size: 13px;
        color: #555;
    }
`;

const LogoCard = styled.div`
    background: #f0f0f0;
    border: 1px solid #ddd;
    border-radius: 8px;
    overflow: hidden;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    flex: 1;
    min-width: 240px;
    max-width: 320px;

    .card-media {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 160px;
        padding: 16px;
    }

    img {
        max-width: 200px;
        max-height: 130px;
        object-fit: contain;
    }

    video {
        max-width: 200px;
        max-height: 130px;
        border-radius: 4px;
    }

    .card-footer {
        padding: 10px 12px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        width: 100%;
        border-top: 1px solid #ddd;
    }

    .card-label {
        font-weight: 600;
        font-size: 13px;
        color: #555;
    }
`;

const TimelineList = styled.ul`
    list-style: disc;
    padding-left: 24px;
    margin: 0;

    li {
        padding: 8px 0;
        font-size: 15px;
        line-height: 1.5;
        color: #444;

        strong {
            color: #222;
        }
    }
`;

// ─── Projects summary ────────────────────────────────────────
const ProjectsRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
    margin: 20px 0 30px 0;
    justify-content: center;

    @media (max-width: 768px) {
        flex-direction: column;
        align-items: center;
    }
`;

// Partenaire-style card (280px, no click)
const PartenaireProjectCard = styled.div`
    display: flex;
    flex-direction: column;
    background-color: rgb(252, 252, 252);
    border-radius: 0px;
    border: solid 1px black;
    overflow: hidden;
    width: 280px;

    @media (max-width: 768px) {
        width: 100%;
    }
`;

// Laureat-style card (200px, clickable with hover)
const LaureatProjectCard = styled.div`
    display: flex;
    flex-direction: column;
    background-color: rgb(252, 252, 252);
    border-radius: 0px;
    border: solid 1px black;
    overflow: hidden;
    width: 200px;
    cursor: pointer;
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
        transform: translateY(-3px);
        box-shadow: 0 4px 8px rgba(0,0,0,0.15);
    }

    @media (max-width: 768px) {
        width: 100%;
        max-width: 300px;
    }
`;

const ProjectTitle = styled.div`
    font-weight: bold;
    padding: 12px;
`;

const PartenaireProjectImage = styled.div`
    height: 200px;
    background-color: #ddd;
    background-image: ${props => props.src ? `url(${props.src})` : 'none'};
    background-size: cover;
    background-position: center;
`;

const LaureatProjectImage = styled.div`
    height: 150px;
    background-color: #ddd;
    background-image: ${props => props.src ? `url(${props.src})` : 'none'};
    background-size: cover;
    background-position: center;
    position: relative;

    &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.1);
        opacity: ${props => props.src ? 0 : 1};
    }
`;

const ProjectInfo = styled.div`
    padding: 12px;
    font-size: 13px;
    color: #666;

    & > div {
        margin-bottom: 5px;
    }
`;

// ─── Ceremony photos carousel ────────────────────────────────
const CP = '/assets/fd100-kit/ceremony-photos/260409_TABASTE_FRENCH_DESIGN_2026_';
const CEREMONY_PHOTOS = [
    { hd: `${CP}1111 1_HD_HORIZONTALE.jpg`, bd: `${CP}1111 1_HD_HORIZONTALE.jpg` },
    { hd: `${CP}1111 1_HD VERTICALE.jpg`, bd: `${CP}1111 1_HD VERTICALE.jpg` },
    ...['0648','0658','0665','0678','0684','0689','0694','0699','0705','0706',
    '0715','0719','0725','0738','0743','0745','0748','0750','0752','0761',
    '0763','0766','0769','0770','0772','0773','0775','0777','0788','0790',
    '0801','0814','0819','0824','0827','0834','0848','0869','0870','0888',
    '0890','0898','0900','0906','0915','0916','0921','0925','0930','0934',
    '0942','0951','0955','0964','0968','0969','0981','0985','0994','1002',
    '1005','1017','1025','1027','1039','1042','1051','1064','1083','1088',
    '1149',
    ].map(n => ({ hd: `${CP}${n}_HD.jpg`, bd: `${CP}${n}_BD.jpg` })),
];

const PhotoGalleryContainer = styled.div`
    margin-bottom: 16px;
`;

const HeroImageWrapper = styled.div`
    position: relative;
    border-radius: 10px;
    overflow: hidden;
    background: #000;
    border: 1px solid #e0e0e0;
`;

const HeroImage = styled.img`
    width: 100%;
    height: auto;
    display: block;
    max-height: 600px;
    object-fit: contain;
    background: #000;
`;

const NavArrow = styled.button`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    ${props => props.direction === 'left' ? 'left: 12px;' : 'right: 12px;'}
    width: 44px;
    height: 44px;
    border-radius: 50%;
    border: none;
    background: rgba(255, 255, 255, 0.85);
    color: #222;
    font-size: 22px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
    z-index: 2;

    &:hover {
        background: white;
        box-shadow: 0 4px 12px rgba(0,0,0,0.25);
    }
`;

const PhotoCounter = styled.div`
    position: absolute;
    bottom: 12px;
    right: 12px;
    background: rgba(0, 0, 0, 0.6);
    color: white;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 13px;
    font-weight: 600;
    z-index: 2;
`;

const ThumbRow = styled.div`
    display: flex;
    gap: 8px;
    margin-top: 10px;
    overflow-x: auto;
    padding-bottom: 4px;

    &::-webkit-scrollbar {
        height: 6px;
    }
    &::-webkit-scrollbar-thumb {
        background: #ccc;
        border-radius: 3px;
    }
`;

const Thumb = styled.div`
    width: 80px;
    height: 80px;
    border-radius: 8px;
    background-size: cover;
    background-position: center;
    background-color: #eee;
    flex-shrink: 0;
    cursor: pointer;
    border: 2.5px solid ${props => props.active ? 'rgb(24, 51, 189)' : 'transparent'};
    transition: border-color 0.2s, opacity 0.2s;
    opacity: ${props => props.active ? 1 : 0.7};

    &:hover {
        border-color: rgb(24, 51, 189);
        opacity: 1;
    }
`;

const DownloadAllBtn = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-top: 10px;
    padding: 8px 16px;
    background: linear-gradient(135deg, rgb(24, 51, 189), #1a237e);
    color: white;
    text-decoration: none;
    border-radius: 5px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    border: none;
    transition: all 0.2s ease;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(24, 51, 189, 0.3);
        color: white;
        text-decoration: none;
    }
`;

// ─── Press section ───────────────────────────────────────────
const PressDocRow = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 12px 0;
    border-bottom: 1px solid #f0f0f0;

    .doc-icon { font-size: 28px; }

    .doc-info {
        flex: 1;
        .doc-title { font-weight: 600; font-size: 15px; color: #333; }
        .doc-format { font-size: 12px; color: #999; }
    }
`;

// ─── Prix du public ──────────────────────────────────────────
const PersonalizedLinkRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 0;
    flex-wrap: wrap;

    .project-label { font-weight: 600; color: #333; }
`;

const CopyableLink = styled.div`
    background: #f0f4ff;
    border: 1px solid #d0d8f0;
    border-radius: 6px;
    padding: 8px 14px;
    font-family: monospace;
    font-size: 13px;
    color: rgb(24, 51, 189);
    word-break: break-all;
    cursor: pointer;
    transition: background 0.2s;

    &:hover { background: #e0e8ff; }
`;

// ─── Prix du public - Live Score ────────────────────────────
const TOTAL_FD100_PROJECTS = 100;

const LiveBadge = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 14px;
    background: #e8f5e9;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 700;
    color: #2e7d32;
    margin-bottom: 20px;

    &::before {
        content: '';
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: #2e7d32;
        animation: scoreLivePulse 2s ease-in-out infinite;
    }

    @keyframes scoreLivePulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.3; }
    }
`;

const TotalStatsRow = styled.div`
    display: flex;
    gap: 12px;
    margin-bottom: 20px;
    flex-wrap: wrap;
`;

const MiniStat = styled.div`
    background: #f8f9ff;
    border: 1px solid #e0e4f0;
    border-radius: 10px;
    padding: 12px 20px;
    text-align: center;
    flex: 1;
    min-width: 100px;
`;

const MiniStatValue = styled.div`
    font-size: 22px;
    font-weight: 800;
    color: rgb(24, 51, 189);
    line-height: 1;
`;

const MiniStatLabel = styled.div`
    font-size: 11px;
    color: #999;
    font-weight: 600;
    margin-top: 4px;
`;

const ScoreCardContainer = styled.div`
    background: white;
    border: 1.5px solid #e0e4f0;
    border-radius: 16px;
    padding: 20px 24px;
    margin-bottom: 14px;
    box-shadow: 0 2px 12px rgba(24, 51, 189, 0.04);
    transition: transform 0.2s, box-shadow 0.2s;

    &:hover {
        transform: translateY(-1px);
        box-shadow: 0 6px 20px rgba(24, 51, 189, 0.08);
    }
`;

const ScoreCardTop = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;

    @media (max-width: 600px) {
        flex-direction: column;
        text-align: center;
    }
`;

const ScoreMiddle = styled.div`
    flex: 1;
    min-width: 0;
`;

const ScoreProjectTitle = styled.div`
    font-weight: 700;
    font-size: 16px;
    color: #1a1a1a;
    margin-bottom: 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const ScoreSubtext = styled.div`
    font-size: 12px;
    color: #999;
    margin-bottom: 10px;
`;

const ScoreProgressTrack = styled.div`
    height: 6px;
    background: #eef1ff;
    border-radius: 3px;
    overflow: hidden;
`;

const ScoreProgressFill = styled.div`
    height: 100%;
    border-radius: 3px;
    background: linear-gradient(90deg, rgb(24, 51, 189), rgb(67, 97, 238));
    transition: width 1s ease-out;
`;

const ScoreVoteDisplay = styled.div`
    text-align: right;
    flex-shrink: 0;
    min-width: 70px;

    @media (max-width: 600px) {
        text-align: center;
    }
`;

const ScoreVoteNumber = styled.div`
    font-size: 28px;
    font-weight: 800;
    color: rgb(24, 51, 189);
    line-height: 1;
`;

const ScoreVoteLabel = styled.div`
    font-size: 11px;
    color: #999;
    font-weight: 600;
    margin-top: 2px;
`;

const ScoreCardBottom = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px solid #f0f2f8;
    flex-wrap: wrap;
`;

const ShareVoteBtn = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 16px;
    background: rgb(24, 51, 189);
    color: white;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    text-decoration: none;
    border: none;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        background: #1a237e;
        transform: translateY(-1px);
        color: white;
        text-decoration: none;
    }

    &:visited, &:active, &:focus {
        color: white;
        text-decoration: none;
    }
`;

const CopyShareBtn = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 16px;
    background: #f0f4ff;
    color: rgb(24, 51, 189);
    border: 1.5px solid #d0d8f0;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        background: #e0e8ff;
        transform: translateY(-1px);
    }
`;

// ─── Exposition form ─────────────────────────────────────────
const ExpoHighlight = styled.div`
    background: #ffffff;
    border: 2px solid rgb(24, 51, 189);
    border-radius: 12px;
    padding: 28px 28px 24px;
    margin-bottom: 36px;
    position: relative;
    box-shadow: 0 4px 20px rgba(24, 51, 189, 0.08);

    .expo-badge {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        background: rgb(24, 51, 189);
        color: white;
        font-size: 12px;
        font-weight: 800;
        text-transform: uppercase;
        letter-spacing: 0.8px;
        padding: 5px 14px;
        border-radius: 20px;
        margin-bottom: 14px;
    }

    .expo-title {
        font-size: 22px;
        font-weight: 800;
        color: #1a1a1a;
        margin-bottom: 10px;
        line-height: 1.3;
    }

    .expo-desc {
        font-size: 15px;
        color: #555;
        line-height: 1.6;
        margin-bottom: 20px;
    }
`;

const ExpoProjectBlock = styled.div`
    margin-bottom: 30px;
    padding: 20px;
    background: #f8f9fa;
    border-radius: 8px;
    border: 1px solid #e0e4f0;
`;

const ExpoProjectTitle = styled.div`
    font-weight: bold;
    font-size: 16px;
    color: #333;
    margin-bottom: 16px;
`;

const PieceCard = styled.div`
    background: white;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 12px;
    display: flex;
    gap: 16px;
    align-items: flex-start;

    @media (max-width: 600px) {
        flex-direction: column;
    }
`;

const PieceThumb = styled.img`
    width: 80px;
    height: 80px;
    object-fit: cover;
    border-radius: 6px;
    flex-shrink: 0;
    background: #eee;
`;

const PieceInfo = styled.div`
    flex: 1;
    min-width: 0;

    .piece-title {
        font-weight: 700;
        font-size: 15px;
        color: #222;
        margin-bottom: 4px;
    }

    .piece-detail {
        font-size: 13px;
        color: #666;
        margin-bottom: 2px;
    }
`;

const PieceActions = styled.div`
    display: flex;
    gap: 6px;
    flex-shrink: 0;
`;

const SmallBtn = styled.button`
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    border: 1px solid #ddd;
    background: ${props => props.danger ? '#fff5f5' : '#f8f9fa'};
    color: ${props => props.danger ? '#c0392b' : '#333'};
    transition: all 0.2s;

    &:hover {
        background: ${props => props.danger ? '#fde8e8' : '#eee'};
    }
`;

const AddPieceBtn = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 10px 20px;
    background: rgb(24, 51, 189);
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    margin-top: 8px;

    &:hover {
        background: #1a237e;
        transform: translateY(-1px);
    }
`;

const FormOverlay = styled.div`
    background: white;
    border: 2px solid rgb(24, 51, 189);
    border-radius: 10px;
    padding: 24px;
    margin-top: 16px;
    margin-bottom: 16px;
`;

const FormTitle = styled.div`
    font-weight: 700;
    font-size: 16px;
    color: rgb(24, 51, 189);
    margin-bottom: 20px;
`;

const FormRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 16px;

    @media (max-width: 600px) {
        grid-template-columns: 1fr;
    }
`;

const FormGroup = styled.div`
    display: flex;
    flex-direction: column;

    label {
        font-size: 13px;
        font-weight: 600;
        color: #555;
        margin-bottom: 4px;
    }

    input, textarea {
        padding: 10px 12px;
        border: 1px solid #ddd;
        border-radius: 6px;
        font-size: 14px;
        font-family: inherit;
        transition: border-color 0.2s;

        &:focus {
            outline: none;
            border-color: rgb(24, 51, 189);
        }
    }

    textarea {
        resize: vertical;
        min-height: 60px;
    }
`;

const PhotoDropZone = styled.div`
    border: 2px dashed ${props => props.isDragOver ? 'rgb(24, 51, 189)' : '#ccc'};
    border-radius: 8px;
    padding: 24px;
    text-align: center;
    color: #888;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s;
    background: ${props => props.isDragOver ? '#f0f4ff' : '#fafafa'};
    margin-bottom: 12px;

    &:hover {
        border-color: rgb(24, 51, 189);
        background: #f0f4ff;
    }
`;

const PhotoPreviewGrid = styled.div`
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    margin-bottom: 12px;
`;

const PhotoPreviewItem = styled.div`
    position: relative;
    width: 90px;
    height: 90px;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: 6px;
        border: 1px solid #ddd;
    }

    .remove-btn {
        position: absolute;
        top: -6px;
        right: -6px;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background: #c0392b;
        color: white;
        border: none;
        font-size: 12px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        line-height: 1;
    }
`;

const FormBtnRow = styled.div`
    display: flex;
    gap: 10px;
    margin-top: 20px;
`;

const SubmitBtn = styled.button`
    padding: 10px 24px;
    background: rgb(24, 51, 189);
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;

    &:hover:not(:disabled) {
        background: #1a237e;
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const CancelBtn = styled.button`
    padding: 10px 24px;
    background: white;
    color: #666;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        background: #f5f5f5;
    }
`;

// ─── Component ───────────────────────────────────────────────
const FD100KitPage = ({
    mode,
    displayName,
    displayLogo,
    partenaireType,
    projects = [],
    kvImages = {},
    genericKvImages = null,
    classicKvImages = null,
    generatingKvs = false,
    definitiveNames = {},
    eventId,
    downloadKV,
    onProjectClick,
    partenaireId,
}) => {
    const { i18n } = useTranslation('common');
    const isFr = i18n.language === 'fr';
    const isPresse = mode === 'presse';
    const isJuryOrOfficiel = mode === 'partenaire' && (partenaireType === 'institution' || partenaireType === 'officiel');

    const [expandedSections, setExpandedSections] = useState({ 1: false, 2: false, 3: false, 4: false, 5: false });
    const [copiedLink, setCopiedLink] = useState(null);
    const [selectedPhoto, setSelectedPhoto] = useState(0);

    // Live votes from Supabase
    const [voteCounts, setVoteCounts] = useState({});
    const [votesLoaded, setVotesLoaded] = useState(false);

    useEffect(() => {
        const fetchVotes = async () => {
            try {
                // Paginate to bypass Supabase's default 1000-row limit
                const all = [];
                const PAGE_SIZE = 1000;
                let from = 0;
                while (true) {
                    const { data } = await supabase
                        .from('fd100_votes')
                        .select('project_id')
                        .range(from, from + PAGE_SIZE - 1);
                    if (!data || data.length === 0) break;
                    all.push(...data);
                    if (data.length < PAGE_SIZE) break;
                    from += PAGE_SIZE;
                }
                const counts = {};
                all.forEach(v => {
                    counts[v.project_id] = (counts[v.project_id] || 0) + 1;
                });
                setVoteCounts(counts);
            } catch (e) {
                console.warn('Failed to fetch votes:', e);
            }
            setVotesLoaded(true);
        };
        fetchVotes();
        const interval = setInterval(fetchVotes, 60000);
        return () => clearInterval(interval);
    }, []);

    const projectRanks = useMemo(() => {
        const sorted = Object.entries(voteCounts).sort(([, a], [, b]) => b - a);
        const ranks = {};
        sorted.forEach(([pid], idx) => { ranks[pid] = idx + 1; });
        return ranks;
    }, [voteCounts]);

    const maxVoteCount = useMemo(() => Math.max(...Object.values(voteCounts), 1), [voteCounts]);
    const totalVotes = useMemo(() => Object.values(voteCounts).reduce((a, b) => a + b, 0), [voteCounts]);
    const projectsWithVotes = useMemo(() => Object.keys(voteCounts).length, [voteCounts]);

    // Top 10 project IDs by votes (stable reference for fetch)
    const top10Pids = useMemo(() => {
        return Object.entries(voteCounts)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 10)
            .map(([pid]) => pid);
    }, [voteCounts]);

    // Shuffled for display
    const top10Shuffled = useMemo(() => {
        const items = [...top10Pids];
        for (let i = items.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [items[i], items[j]] = [items[j], items[i]];
        }
        return items;
    }, [top10Pids]);

    // Fetch names + images for top 10 projects
    const [top10Data, setTop10Data] = useState({});
    const top10PidsKey = top10Pids.join(',');
    useEffect(() => {
        if (!top10Pids.length) return;
        Promise.all([
            supabase
                .from('fd100_project_names')
                .select('project_id, name_definitive_fr, name_definitive_en, designer_definitive')
                .eq('event_id', eventId)
                .in('project_id', top10Pids),
            api.getProjectsBasicData({ projectIds: top10Pids }),
        ]).then(([namesRes, basicRes]) => {
            const data = {};
            if (namesRes.data) {
                namesRes.data.forEach(item => {
                    data[item.project_id] = {
                        name_fr: item.name_definitive_fr,
                        name_en: item.name_definitive_en,
                        designer: item.designer_definitive,
                    };
                });
            }
            if (basicRes.data) {
                Object.entries(basicRes.data).forEach(([pid, info]) => {
                    if (!data[pid]) data[pid] = {};
                    data[pid].imagePath = info.imagePath;
                });
            }
            setTop10Data(data);
        }).catch(e => console.warn('Failed to fetch top 10 data:', e));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [top10PidsKey, eventId]);

    const handleDownloadAllPhotos = useCallback(async (quality = 'hd') => {
        for (const photo of CEREMONY_PHOTOS) {
            const src = quality === 'hd' ? photo.hd : photo.bd;
            const a = document.createElement('a');
            a.href = src;
            a.download = src.split('/').pop();
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            await new Promise(r => setTimeout(r, 300));
        }
    }, []);

    const toggleSection = (num) => {
        setExpandedSections(prev => ({ ...prev, [num]: !prev[num] }));
    };

    // ─── Exposition pieces state ─────────────────────────────
    const isFabricant = mode === 'partenaire' && partenaireType === 'fabricant';
    const isExpoEligible = mode === 'partenaire' && (partenaireType === 'fabricant' || partenaireType === 'distributeur' || partenaireType === 'editeur');
    const [expoPieces, setExpoPieces] = useState([]);
    const [expoLoading, setExpoLoading] = useState(false);
    const [expoFormOpen, setExpoFormOpen] = useState(null); // projectId or null
    const [expoEditId, setExpoEditId] = useState(null);
    const [expoSubmitting, setExpoSubmitting] = useState(false);
    const [expoForm, setExpoForm] = useState({
        nom_piece: '', typologie: '', date_creation: '', designer_names: '', editeur: '',
        materiaux: '', dimensions: '',
    });
    const [expoPhotos, setExpoPhotos] = useState([]); // File objects for new upload
    const [expoExistingPhotos, setExpoExistingPhotos] = useState([]); // URLs for edit mode
    const [expoDragOver, setExpoDragOver] = useState(false);

    const expoFileInputRef = useRef(null);

    // ─── Exposition contact state ─────────────────────────────
    const [expoContact, setExpoContact] = useState({
        contact_prenom: '', contact_nom: '', contact_email: '', contact_telephone: '',
    });
    const [expoContactSaving, setExpoContactSaving] = useState(false);
    const [expoContactSaved, setExpoContactSaved] = useState(false);
    const [expoContactOpen, setExpoContactOpen] = useState(false);

    // Fetch existing pieces + contact info + track visit
    useEffect(() => {
        if (!isExpoEligible || !partenaireId) return;
        setExpoLoading(true);
        // Fetch pieces
        supabase
            .from('fd100_exposition_pieces')
            .select('*')
            .eq('partenaire_id', partenaireId)
            .order('created_at', { ascending: false })
            .then(({ data }) => {
                setExpoPieces(data || []);
                setExpoLoading(false);
            });
        // Fetch contact from partenaire row
        supabase
            .from('fd100_partenaires')
            .select('contact_prenom, contact_nom, contact_email, contact_telephone')
            .eq('id', partenaireId)
            .single()
            .then(({ data }) => {
                if (data) {
                    setExpoContact({
                        contact_prenom: data.contact_prenom || '',
                        contact_nom: data.contact_nom || '',
                        contact_email: data.contact_email || '',
                        contact_telephone: data.contact_telephone || '',
                    });
                }
            });
        // Track visit
        supabase
            .from('fd100_partenaires')
            .update({ last_visited_at: new Date().toISOString() })
            .eq('id', partenaireId)
            .then(() => {});
    }, [isExpoEligible, partenaireId]);

    const handleExpoContactSave = useCallback(async () => {
        if (!partenaireId) return;
        setExpoContactSaving(true);
        await supabase
            .from('fd100_partenaires')
            .update({
                contact_prenom: expoContact.contact_prenom,
                contact_nom: expoContact.contact_nom,
                contact_email: expoContact.contact_email,
                contact_telephone: expoContact.contact_telephone,
                contact_saved_at: new Date().toISOString(),
            })
            .eq('id', partenaireId);
        setExpoContactSaving(false);
        setExpoContactSaved(true);
        setTimeout(() => setExpoContactSaved(false), 3000);
    }, [partenaireId, expoContact]);

    const resetExpoForm = useCallback(() => {
        setExpoForm({ nom_piece: '', typologie: '', date_creation: '', designer_names: '', editeur: '', materiaux: '', dimensions: '' });
        setExpoPhotos([]);
        setExpoExistingPhotos([]);
        setExpoFormOpen(null);
        setExpoEditId(null);
    }, []);

    const openExpoAdd = useCallback((projectId) => {
        resetExpoForm();
        setExpoFormOpen(projectId);
    }, [resetExpoForm]);

    const openExpoEdit = useCallback((piece) => {
        setExpoForm({
            nom_piece: piece.nom_piece || '',
            typologie: piece.typologie || '',
            date_creation: piece.date_creation || '',
            designer_names: piece.designer_names || '',
            editeur: piece.editeur || '',
            materiaux: piece.materiaux || '',
            dimensions: piece.dimensions || '',
        });
        setExpoExistingPhotos(piece.photo_urls || []);
        setExpoPhotos([]);
        setExpoFormOpen(piece.project_id);
        setExpoEditId(piece.id);
    }, []);

    const handleExpoPhotoDrop = useCallback((e) => {
        e.preventDefault();
        setExpoDragOver(false);
        const files = Array.from(e.dataTransfer?.files || e.target?.files || [])
            .filter(f => f.type.startsWith('image/'));
        if (files.length) setExpoPhotos(prev => [...prev, ...files]);
    }, []);

    const removeExpoNewPhoto = useCallback((idx) => {
        setExpoPhotos(prev => prev.filter((_, i) => i !== idx));
    }, []);

    const removeExpoExistingPhoto = useCallback((url) => {
        setExpoExistingPhotos(prev => prev.filter(u => u !== url));
    }, []);

    const handleExpoSubmit = useCallback(async (projectId) => {
        setExpoSubmitting(true);
        try {
            // Upload new photos
            const uploadedUrls = [];
            for (const file of expoPhotos) {
                const ext = file.name.split('.').pop().toLowerCase();
                const fileName = `${eventId}/${partenaireId}/${Date.now()}_${Math.random().toString(36).slice(2, 8)}.${ext}`;
                const { error: upErr } = await supabase.storage
                    .from('fd100-exposition-photos')
                    .upload(fileName, file);
                if (upErr) { console.error('Upload error:', upErr); continue; }
                const { data: { publicUrl } } = supabase.storage
                    .from('fd100-exposition-photos')
                    .getPublicUrl(fileName);
                uploadedUrls.push(publicUrl);
            }

            const allPhotos = [...expoExistingPhotos, ...uploadedUrls];
            const row = {
                partenaire_id: partenaireId,
                project_id: projectId,
                event_id: eventId,
                nom_piece: expoForm.nom_piece,
                typologie: expoForm.typologie,
                date_creation: expoForm.date_creation,
                designer_names: expoForm.designer_names,
                editeur: expoForm.editeur,
                materiaux: expoForm.materiaux,
                dimensions: expoForm.dimensions,
                photo_urls: allPhotos,
                updated_at: new Date().toISOString(),
            };

            if (expoEditId) {
                await supabase.from('fd100_exposition_pieces').update(row).eq('id', expoEditId);
            } else {
                await supabase.from('fd100_exposition_pieces').insert(row);
            }

            // Refresh
            const { data } = await supabase
                .from('fd100_exposition_pieces')
                .select('*')
                .eq('partenaire_id', partenaireId)
                .order('created_at', { ascending: false });
            setExpoPieces(data || []);
            resetExpoForm();
        } catch (err) {
            console.error('Expo submit error:', err);
        }
        setExpoSubmitting(false);
    }, [expoPhotos, expoExistingPhotos, expoForm, expoEditId, partenaireId, eventId, resetExpoForm]);

    const handleExpoDelete = useCallback(async (pieceId) => {
        if (!window.confirm(isFr ? 'Supprimer cette pièce ?' : 'Delete this piece?')) return;
        await supabase.from('fd100_exposition_pieces').delete().eq('id', pieceId);
        setExpoPieces(prev => prev.filter(p => p.id !== pieceId));
    }, [isFr]);

    // ─── Name helpers (from PartenairesView) ─────────────────
    const getProjectName = (project) => {
        const definitive = definitiveNames[project._id];
        if (definitive) {
            return isFr
                ? definitive.name_fr
                : (definitive.name_en || definitive.name_fr);
        }
        return !isFr && project.name_en
            ? project.name_en
            : (project.name || (isFr ? 'Projet sans nom' : 'Unnamed project'));
    };

    const getDesignerName = (project, user) => {
        const definitive = definitiveNames[project._id];
        if (definitive?.designer) return definitive.designer;
        return getEventDesignerDisplayName(project, user);
    };

    const getDefinitiveTypology = (project) => {
        const definitive = definitiveNames[project._id];
        if (definitive) {
            return isFr
                ? definitive.typology_fr
                : (definitive.typology_en || definitive.typology_fr);
        }
        return project.typologie;
    };

    const handleCopyLink = (link, id) => {
        navigator.clipboard.writeText(link).then(() => {
            setCopiedLink(id);
            setTimeout(() => setCopiedLink(null), 2000);
        });
    };

    // ─── DateGatedContent ────────────────────────────────────
    const DateGatedContent = ({ gateDate, children }) => {
        const now = new Date();
        if (now >= gateDate) return children;
        const dateStr = gateDate.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
        return (
            <ComingSoonPlaceholder>
                <span className="lock-icon">&#128274;</span>
                {isFr
                    ? `Disponible à partir du ${dateStr}`
                    : `Available from ${dateStr}`}
            </ComingSoonPlaceholder>
        );
    };

    // ─── Accordion helper ────────────────────────────────────
    const renderAccordion = (number, title, content) => (
        <AccordionWrapper key={number}>
            <AccordionHeader onClick={() => toggleSection(number)}>
                <AccordionLeft>
                    <SectionNumber>{number}.</SectionNumber>
                    <SectionTitle>{title}</SectionTitle>
                </AccordionLeft>
                <ChevronIcon isOpen={expandedSections[number]}>&#9660;</ChevronIcon>
            </AccordionHeader>
            <AccordionContent isOpen={expandedSections[number]}>
                {content}
            </AccordionContent>
        </AccordionWrapper>
    );

    // ═════════════════════════════════════════════════════════
    // SECTION 1: Cérémonie de révélation
    // ═════════════════════════════════════════════════════════
    const renderSection1 = () => (
        <>
            <SubsectionTitle>
                {isFr ? 'Photos de la cérémonie' : 'Ceremony photos'}
            </SubsectionTitle>
            <DateGatedContent gateDate={DATE_GATES.CEREMONY_PHOTOS}>
                <PhotoGalleryContainer>
                    <HeroImageWrapper>
                        <HeroImage
                            src={CEREMONY_PHOTOS[selectedPhoto].hd}
                            alt={isFr ? `Photo cérémonie ${selectedPhoto + 1}` : `Ceremony photo ${selectedPhoto + 1}`}
                        />
                        {selectedPhoto > 0 && (
                            <NavArrow direction="left" onClick={() => setSelectedPhoto(prev => prev - 1)}>
                                &#8249;
                            </NavArrow>
                        )}
                        {selectedPhoto < CEREMONY_PHOTOS.length - 1 && (
                            <NavArrow direction="right" onClick={() => setSelectedPhoto(prev => prev + 1)}>
                                &#8250;
                            </NavArrow>
                        )}
                        <PhotoCounter>{selectedPhoto + 1} / {CEREMONY_PHOTOS.length}</PhotoCounter>
                    </HeroImageWrapper>
                    <ThumbRow>
                        {CEREMONY_PHOTOS.map((photo, idx) => (
                            <Thumb
                                key={idx}
                                active={idx === selectedPhoto}
                                style={{ backgroundImage: `url(${photo.bd})` }}
                                onClick={() => setSelectedPhoto(idx)}
                            />
                        ))}
                    </ThumbRow>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <DownloadBtn
                            href={CEREMONY_PHOTOS[selectedPhoto].hd}
                            download={CEREMONY_PHOTOS[selectedPhoto].hd.split('/').pop()}
                        >
                            {isFr ? 'HD' : 'HD'}
                        </DownloadBtn>
                        <DownloadBtn
                            href={CEREMONY_PHOTOS[selectedPhoto].bd}
                            download={CEREMONY_PHOTOS[selectedPhoto].bd.split('/').pop()}
                            style={{ background: 'linear-gradient(135deg, #6c757d, #495057)' }}
                        >
                            {isFr ? 'BD' : 'SD'}
                        </DownloadBtn>
                        <DownloadAllBtn onClick={() => handleDownloadAllPhotos('hd')}>
                            {isFr ? `Toutes HD (${CEREMONY_PHOTOS.length})` : `All HD (${CEREMONY_PHOTOS.length})`}
                        </DownloadAllBtn>
                        <DownloadAllBtn
                            onClick={() => handleDownloadAllPhotos('bd')}
                            style={{ background: 'linear-gradient(135deg, #6c757d, #495057)' }}
                        >
                            {isFr ? `Toutes BD (${CEREMONY_PHOTOS.length})` : `All SD (${CEREMONY_PHOTOS.length})`}
                        </DownloadAllBtn>
                    </div>
                </PhotoGalleryContainer>
            </DateGatedContent>
        </>
    );

    // ═════════════════════════════════════════════════════════
    // SECTION 2: Éléments de communication & Charte graphique
    // ═════════════════════════════════════════════════════════
    const renderSection2 = () => (
        <>
            {/* Personalized KV visuals */}
            {!isPresse && <SubsectionTitle>
                {isFr ? 'Visuels personnalisés' : 'Personalized visuals'} ({projects.length * 2 + (genericKvImages ? 2 : 0) + (classicKvImages ? 2 : 0)})
            </SubsectionTitle>}
            {!isPresse && (generatingKvs ? (
                    <div style={{ padding: '40px', textAlign: 'center' }}>
                        <Spinner />
                        <p style={{ marginTop: '16px', color: '#666' }}>
                            {isFr ? 'Génération des Key Visuals...' : 'Generating Key Visuals...'}
                        </p>
                    </div>
                ) : projects.length > 0 ? (
                    projects.map((projectData, index) => {
                        const projectId = projectData.project._id;
                        const projectKvs = kvImages[projectId];
                        const projectName = getProjectName(projectData.project);
                        const sanitizedName = projectName.replace(/[^a-zA-Z0-9]/g, '_');

                        return (
                            <KVProjectBlock key={index}>
                                <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '16px', color: '#333' }}>
                                    {projectName}
                                </div>
                                <KVRow>
                                    <KVCard>
                                        {projectKvs?.normal ? (
                                            <img src={projectKvs.normal} alt={`${projectName} - Gris`} />
                                        ) : (
                                            <VisualCard>
                                                <div className="label">{isFr ? 'Format Gris' : 'Gray Format'}</div>
                                                <div className="placeholder">{isFr ? 'Non disponible' : 'Not available'}</div>
                                            </VisualCard>
                                        )}
                                        {projectKvs?.normal && downloadKV && (
                                            <DownloadBtn
                                                as="button"
                                                onClick={() => downloadKV(projectKvs.normal, sanitizedName, 'gris')}
                                            >
                                                {isFr ? 'Télécharger (Gris)' : 'Download (Gray)'}
                                            </DownloadBtn>
                                        )}
                                    </KVCard>
                                    <KVCard>
                                        {projectKvs?.inverted ? (
                                            <img src={projectKvs.inverted} alt={`${projectName} - Bleu`} />
                                        ) : (
                                            <VisualCard>
                                                <div className="label">{isFr ? 'Format Bleu' : 'Blue Format'}</div>
                                                <div className="placeholder">{isFr ? 'Non disponible' : 'Not available'}</div>
                                            </VisualCard>
                                        )}
                                        {projectKvs?.inverted && downloadKV && (
                                            <DownloadBtn
                                                as="button"
                                                onClick={() => downloadKV(projectKvs.inverted, sanitizedName, 'bleu')}
                                            >
                                                {isFr ? 'Télécharger (Bleu)' : 'Download (Blue)'}
                                            </DownloadBtn>
                                        )}
                                    </KVCard>
                                </KVRow>
                            </KVProjectBlock>
                        );
                    })
                ) : genericKvImages ? (
                    <KVProjectBlock>
                        <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '16px', color: '#333' }}>
                            {isFr ? 'Visuel générique Le FD100' : 'Generic FD100 Visual'}
                        </div>
                        <KVRow>
                            <KVCard>
                                <img src={genericKvImages.normal} alt={`${displayName} - Gris`} />
                                {downloadKV && (
                                    <DownloadBtn
                                        as="button"
                                        onClick={() => downloadKV(genericKvImages.normal, displayName.replace(/[^a-zA-Z0-9]/g, '_'), 'gris')}
                                    >
                                        {isFr ? 'Télécharger (Gris)' : 'Download (Gray)'}
                                    </DownloadBtn>
                                )}
                            </KVCard>
                            <KVCard>
                                <img src={genericKvImages.inverted} alt={`${displayName} - Bleu`} />
                                {downloadKV && (
                                    <DownloadBtn
                                        as="button"
                                        onClick={() => downloadKV(genericKvImages.inverted, displayName.replace(/[^a-zA-Z0-9]/g, '_'), 'bleu')}
                                    >
                                        {isFr ? 'Télécharger (Bleu)' : 'Download (Blue)'}
                                    </DownloadBtn>
                                )}
                            </KVCard>
                        </KVRow>
                    </KVProjectBlock>
                ) : (
                    <StaticTextBlock>
                        {isFr ? 'Aucun projet lié.' : 'No linked projects.'}
                    </StaticTextBlock>
                ))}

            {/* Classic declination (gray/blue bg) - demo */}
            {!isPresse && classicKvImages && (
                <KVProjectBlock>
                    <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '16px', color: '#333' }}>
                        {isFr ? 'Déclinaison classique Le FD100' : 'Classic FD100 Declination'}
                    </div>
                    <KVRow>
                        <KVCard>
                            <img src={classicKvImages.normal} alt={`${displayName} - Gris classique`} />
                            {downloadKV && (
                                <DownloadBtn
                                    as="button"
                                    onClick={() => downloadKV(classicKvImages.normal, displayName.replace(/[^a-zA-Z0-9]/g, '_') + '_classic', 'gris')}
                                >
                                    {isFr ? 'Télécharger (Gris)' : 'Download (Gray)'}
                                </DownloadBtn>
                            )}
                        </KVCard>
                        <KVCard>
                            <img src={classicKvImages.inverted} alt={`${displayName} - Bleu classique`} />
                            {downloadKV && (
                                <DownloadBtn
                                    as="button"
                                    onClick={() => downloadKV(classicKvImages.inverted, displayName.replace(/[^a-zA-Z0-9]/g, '_') + '_classic', 'bleu')}
                                >
                                    {isFr ? 'Télécharger (Bleu)' : 'Download (Blue)'}
                                </DownloadBtn>
                            )}
                        </KVCard>
                    </KVRow>
                </KVProjectBlock>
            )}

            {/* FD100 social visuals */}
            <SubsectionTitle>
                {isFr ? 'Visuels Le FD100' : 'FD100 Visuals'}
            </SubsectionTitle>
            <VisualsGrid>
                {[
                    { label: 'Format Facebook FR', src: '/assets/fd100-kit/visuals/facebook/FD100-POST-FACEBOOK-FR.jpg' },
                    { label: 'Format Facebook EN', src: '/assets/fd100-kit/visuals/facebook/FD100-POST-FACEBOOK-EN.jpg' },
                    { label: 'Format Instagram FR', src: '/assets/fd100-kit/visuals/instagram/FD100-POST-INSTA-FR.jpg' },
                    { label: 'Format Instagram Paysage FR', src: '/assets/fd100-kit/visuals/instagram/FD100-POST-INSTA-PAYSAGE-FR.jpg' },
                    { label: 'Format Instagram Paysage EN', src: '/assets/fd100-kit/visuals/instagram/FD100-POST-INSTA-PAYSAGE-EN.jpg' },
                    { label: 'Format Story Instagram FR', src: '/assets/fd100-kit/visuals/instagram/FD100-STORY-INSTA-FR.jpg' },
                    { label: 'Format Story Instagram EN', src: '/assets/fd100-kit/visuals/instagram/FD100-STORY-INSTA-EN.jpg' },
                    { label: 'Format LinkedIn FR', src: '/assets/fd100-kit/visuals/linkedin/FD100-POST-LINKEDIN-FR.jpg' },
                    { label: 'Format LinkedIn EN', src: '/assets/fd100-kit/visuals/linkedin/FD100-POST-LINKEDIN-EN.jpg' },
                ].map((item, idx) => (
                    <VisualImageCard key={idx}>
                        <img src={item.src} alt={item.label} />
                        <div className="card-footer">
                            <span className="card-label">{item.label}</span>
                            <DownloadBtn
                                href={item.src}
                                download={item.label.replace(/\s/g, '_') + '.jpg'}
                            >
                                {isFr ? 'Télécharger' : 'Download'}
                            </DownloadBtn>
                        </div>
                    </VisualImageCard>
                ))}
            </VisualsGrid>

            {/* FD100 logos */}
            <SubsectionTitle>
                {isFr ? 'Logos Le FD100' : 'FD100 Logos'}
            </SubsectionTitle>
            <LogosRow>
                <LogoCard>
                    <div className="card-media">
                        <img src="/assets/fd100-kit/logos/SD25-FD100-Logo-Story.png" alt="Logo FD100 PNG" />
                    </div>
                    <div className="card-footer">
                        <span className="card-label">Logo PNG</span>
                        <DownloadBtn
                            href="/assets/fd100-kit/logos/SD25-FD100-Logo-Story.png"
                            download="FD100_Logo.png"
                        >
                            {isFr ? 'Télécharger' : 'Download'}
                        </DownloadBtn>
                    </div>
                </LogoCard>
                <LogoCard>
                    <div className="card-media">
                        <video autoPlay loop muted playsInline>
                            <source src={isFr
                                ? '/assets/fd100-kit/logos/SD25-FD100-Animation-Logo-FR.mp4'
                                : '/assets/fd100-kit/logos/SD25-FD100-Animation-Logo-EN.mp4'} type="video/mp4" />
                        </video>
                    </div>
                    <div className="card-footer">
                        <span className="card-label">{isFr ? 'Logo Animé' : 'Animated Logo'}</span>
                        <div style={{ display: 'flex', gap: '6px' }}>
                            <DownloadBtn
                                href="/assets/fd100-kit/logos/SD25-FD100-Animation-Logo-FR.mp4"
                                download="FD100_Logo_Anime_FR.mp4"
                            >
                                FR
                            </DownloadBtn>
                            <DownloadBtn
                                href="/assets/fd100-kit/logos/SD25-FD100-Animation-Logo-EN.mp4"
                                download="FD100_Logo_Anime_EN.mp4"
                            >
                                EN
                            </DownloadBtn>
                        </div>
                    </div>
                </LogoCard>
            </LogosRow>

            {/* Language elements */}
            <SubsectionTitle>
                {isFr ? 'Éléments de langage' : 'Language elements - Newsletter and social media'}
            </SubsectionTitle>
            {!isPresse && mode === 'partenaire' && partenaireType !== 'institution' && partenaireType !== 'officiel' && (
                <StaticTextBlock>
                    <p><strong>{isFr
                        ? 'A partir de 18h30 CET le 9 avril vous êtes invités à relayer l\'information sur les lauréats de la 4ème édition du prix Le FD100.'
                        : 'Starting at 6:30 PM CET on April 9, you are invited to share the announcement of the winners of the 4th edition of the Le FD100 Prize.'}</strong></p>
                    <ul style={{ paddingLeft: '20px', margin: '12px 0' }}>
                        <li>{isFr
                            ? 'Partage sur vos comptes Instagram, LinkedIn, Facebook des publications posts et stories faites par Le French Design en lien avec Le FD100'
                            : 'Please share on your Instagram, LinkedIn, and Facebook accounts the posts and stories published by Le French Design related to Le FD100'}</li>
                        <li>{isFr
                            ? 'Publication sur vos réseaux sociaux de votre visuel personnalisé avec votre logo, créé pour révéler la sélection de votre projet lauréat'
                            : 'Publish on your own social media channels your personalized visual including your logo, created to highlight the selection of your award-winning project'}</li>
                        <li>{isFr
                            ? 'Nous vous encourageons à reposter si votre lauréat publie aussi le visuel du projet sur lequel vous avez collaboré'
                            : 'If the laureate also shares the visual of the project you collaborated on, we encourage you to repost their publication'}</li>
                        <li>{isFr
                            ? 'Nous vous invitons à envoyer un Email annonce à votre communauté et/ou votre Newsletter'
                            : 'We also invite you to send an announcement email to your community and/or feature the news in your newsletter'}</li>
                    </ul>
                </StaticTextBlock>
            )}
            {!isJuryOrOfficiel && !isPresse && <StaticTextBlock>
                <p><b>{isFr ? 'Texte pour votre newsletter ' : 'Text for your newsletter '}:</b></p>
                <br />
                <p style={{ color: '#666', fontStyle: 'italic', marginBottom: '12px' }}>
                    {isFr
                        ? 'Ce texte est pensé pour correspondre à une annonce générique. Vous pouvez le conserver ou le modifier.'
                        : 'This is a generic announcement. Feel free to adapt it to your needs.'}
                </p>
                <p><b>FR</b></p>
                {mode === 'partenaire' ? (
                    <p style={{ fontStyle: 'italic' }}>Nous sommes honorés d'annoncer que le projet dans le cadre duquel nous avons collaboré avec XXX est lauréat de la 4ème édition du prix Le FRENCH DESIGN 100. Seul prix célébrant le rayonnement international des designers, architectes et fabricants français, Le FRENCH DESIGN 100 distingue les cent meilleurs projets de design d'objets (mobilier, décoration...) et d'espaces (hôtels, boutiques, restaurants...) qui valorisent le design français à travers le monde. Découvrez l'ensemble des lauréats sur l'Instagram @lefrenchdesign. Nous sommes heureux de poursuivre, en 2026, notre engagement aux côtés des designers !</p>
                ) : (
                    <p style={{ fontStyle: 'italic' }}>Nous sommes honorés d'annoncer que nous sommes lauréats de la 4ème édition du prix Le FRENCH DESIGN 100. Unique prix célébrant le rayonnement international des designers, architectes et fabricants français, Le FRENCH DESIGN 100 distingue les cent meilleurs projets de design d'objets (mobilier, décoration...) et d'espaces (hôtels, boutiques, restaurants...) qui valorisent le design français à travers le monde.</p>
                )}
                <br />
                <p><b>EN</b></p>
                {mode === 'partenaire' ? (
                    <p style={{ fontStyle: 'italic' }}>We are very honoured to announce that the project we collaborated on with XXX have been awarded in the 4th edition of Le FRENCH DESIGN 100 awards – 100 Objects and Interior design projects that make French creation stand out on the international scene. The only accolade that rewards the global efforts of its designers, architects, and French manufacturers, it has set itself the task of selecting the top 100 design projects - objects (furniture, decoration, etc.) and spaces (hotels, shops, restaurants, etc.) - that shine a light on French creation internationally. Congratulations to all the laureates of this 4th selection!</p>
                ) : (
                    <p style={{ fontStyle: 'italic' }}>We are very honoured to announce that we have been awarded in the 4th edition of Le FRENCH DESIGN 100 awards - 100 Objects and Interior design projects that make French creation stand out on the international scene. The only accolade that rewards the global efforts of its designers, architects, and French manufacturers, it has set itself the task of selecting the top 100 design projects - objects (furniture, decoration, etc.) and spaces (hotels, shops, restaurants, etc.) - that shine a light on French creation internationally.</p>
                )}
            </StaticTextBlock>}
            <StaticTextBlock>
                <p><b>{isFr ? 'Liens site et réseaux sociaux Le FRENCH DESIGN' : 'Le FRENCH DESIGN website and social media links'}:</b></p>
                <br />
                <p><b>{isFr ? 'Site internet' : 'Website'}</b> : <a href="https://lefrenchdesign.org" target="_blank" rel="noopener noreferrer" style={{ color: '#2980b9' }}>lefrenchdesign.org</a></p>
                <br />
                <p><b>{isFr ? 'Réseaux sociaux' : 'Social media'}</b> :</p>
                <p>Instagram - <a href="https://www.instagram.com/lefrenchdesign/" target="_blank" rel="noopener noreferrer" style={{ color: '#2980b9' }}><b>@lefrenchdesign</b></a></p>
                <p>Facebook - <a href="https://www.facebook.com/lefrenchdesign/" target="_blank" rel="noopener noreferrer" style={{ color: '#2980b9' }}><b>@lefrenchdesign</b></a></p>
                <p>LinkedIn - <a href="https://www.linkedin.com/company/lefrenchdesign/" target="_blank" rel="noopener noreferrer" style={{ color: '#2980b9' }}><b>@lefrenchdesign</b></a></p>
                <br />
                <p>{isFr
                    ? <>Lors de vos publications sur les réseaux sociaux, merci de mentionner les comptes <span style={{ color: '#2980b9' }}>@lefrenchdesign</span> et d'utiliser les hashtags</>
                    : <>When posting on social media, please mention the <span style={{ color: '#2980b9' }}>@lefrenchdesign</span> accounts and use the hashtags</>} <b style={{ color: '#2980b9' }}>#lefrenchdesign #LeFD100</b></p>
            </StaticTextBlock>

            {/* Catalogue */}
            <SubsectionTitle>
                {isFr ? 'Catalogue' : 'Catalogue'}
            </SubsectionTitle>
            <DateGatedContent gateDate={DATE_GATES.CATALOGUE}>
                <div style={{ marginBottom: '16px' }}>
                    <a href="/assets/fd100-kit/SD26-FD100-Catalogue.pdf" target="_blank" rel="noopener noreferrer">
                        <img
                            src="/assets/fd100-kit/SD26-FD100-Catalogue-cover-001.jpg"
                            alt={isFr ? 'Couverture du catalogue FD100' : 'FD100 Catalogue cover'}
                            style={{
                                width: '100%',
                                maxWidth: '500px',
                                display: 'block',
                                margin: '0 auto',
                                borderRadius: '8px',
                                border: '1px solid #ddd',
                                cursor: 'pointer',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            }}
                        />
                    </a>
                    <div style={{ marginTop: '12px', textAlign: 'center' }}>
                        <DownloadBtn
                            href="/assets/fd100-kit/SD26-FD100-Catalogue.pdf"
                            download="FD100_Catalogue_4e_Edition.pdf"
                        >
                            {isFr ? 'Télécharger le catalogue (PDF)' : 'Download catalogue (PDF)'}
                        </DownloadBtn>
                    </div>
                </div>
            </DateGatedContent>
        </>
    );

    // ═════════════════════════════════════════════════════════
    // SECTION 3: Presse
    // ═════════════════════════════════════════════════════════
    const renderSection3 = () => (
        <>
            {/* Communiqué de Presse */}
            <SubsectionTitle>
                {isFr ? 'Communiqué de Presse' : 'Press Release'}
            </SubsectionTitle>
            <PressDocRow>
                <div className="doc-icon">&#128196;</div>
                <div className="doc-info">
                    <div className="doc-title">{isFr ? 'Communiqué de Presse FR' : 'Press Release FR'}</div>
                    <div className="doc-format">PDF</div>
                </div>
                <DownloadBtn
                    href="/assets/fd100-kit/presse/SD25-FD100-CP-FR-RVB.pdf"
                    download="FD100_Communique_Presse_FR.pdf"
                >
                    {isFr ? 'Télécharger' : 'Download'}
                </DownloadBtn>
            </PressDocRow>
            <PressDocRow>
                <div className="doc-icon">&#128196;</div>
                <div className="doc-info">
                    <div className="doc-title">{isFr ? 'Communiqué de Presse EN' : 'Press Release EN'}</div>
                    <div className="doc-format">PDF</div>
                </div>
                <DownloadBtn
                    href="/assets/fd100-kit/presse/SD25-FD100-CP-EN-RVB.pdf"
                    download="FD100_Communique_Presse_EN.pdf"
                >
                    {isFr ? 'Télécharger' : 'Download'}
                </DownloadBtn>
            </PressDocRow>

            {/* Dossier de Presse */}
            <SubsectionTitle>
                {isFr ? 'Dossier de Presse' : 'Press Kit'}
            </SubsectionTitle>
            <PressDocRow>
                <div className="doc-icon">&#128218;</div>
                <div className="doc-info">
                    <div className="doc-title">{isFr ? 'Dossier de Presse FR' : 'Press Kit FR'}</div>
                    <div className="doc-format">PDF</div>
                </div>
                <DownloadBtn
                    href="/assets/fd100-kit/presse/SD25-FD100-DP1.pdf"
                    download="FD100_Dossier_Presse_FR.pdf"
                >
                    {isFr ? 'Télécharger' : 'Download'}
                </DownloadBtn>
            </PressDocRow>
            <PressDocRow>
                <div className="doc-icon">&#128218;</div>
                <div className="doc-info">
                    <div className="doc-title">{isFr ? 'Dossier de Presse EN' : 'Press Kit EN'}</div>
                    <div className="doc-format">PDF</div>
                </div>
                <DownloadBtn
                    href="/assets/fd100-kit/presse/SD25-FD100-DP-EN-1.pdf"
                    download="FD100_Dossier_Presse_EN.pdf"
                >
                    {isFr ? 'Télécharger' : 'Download'}
                </DownloadBtn>
            </PressDocRow>

            {/* Retombées Presse */}
            <SubsectionTitle>
                {isFr ? 'Retombées Presse' : 'Press Coverage'}
            </SubsectionTitle>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px' }}>
                {[
                    { title: 'Madame Figaro', date: isFr ? '13 février 2026' : 'February 13, 2026', file: 'Madame-Figaro-13-Fevrier-2026.pdf' },
                    { title: 'Madame Figaro', date: isFr ? '3 avril 2026' : 'April 3, 2026', file: 'Madame-Figaro-3-Avril-2026.pdf' },
                    { title: 'Architectures à vivre', date: isFr ? 'Mars-Avril 2026' : 'March-April 2026', file: 'Architectures-a-vivre-Mars-Avril-2026.pdf' },
                    { title: 'Décors & Sens', date: isFr ? 'Mars-Avril 2026' : 'March-April 2026', file: 'Decors-et-Sens-Mars-Avril-2026.pdf' },
                ].map((article, i) => (
                    <div key={i} style={{
                        background: 'white',
                        borderRadius: '12px',
                        border: '1px solid #e0e0e0',
                        overflow: 'hidden',
                        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.1)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.06)'; }}
                    >
                        <a
                            href={`/assets/fd100-kit/presse/retombees/${article.file}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ display: 'block', textDecoration: 'none' }}
                        >
                            <div style={{
                                background: '#f5f5f5',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden',
                            }}>
                                <object
                                    data={`/assets/fd100-kit/presse/retombees/${article.file}`}
                                    type="application/pdf"
                                    style={{
                                        width: '100%',
                                        height: '380px',
                                        pointerEvents: 'none',
                                        display: 'block',
                                    }}
                                >
                                    <div style={{
                                        height: '380px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#bbb',
                                        fontSize: '48px',
                                    }}>
                                        &#128196;
                                    </div>
                                </object>
                            </div>
                        </a>
                        <div style={{
                            padding: '14px 16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '10px',
                            borderTop: '1px solid #eee',
                        }}>
                            <div>
                                <div style={{ fontWeight: 600, fontSize: '14px', color: '#333' }}>{article.title}</div>
                                <div style={{ fontSize: '12px', color: '#999', marginTop: '2px' }}>{article.date}</div>
                            </div>
                            <DownloadBtn
                                href={`/assets/fd100-kit/presse/retombees/${article.file}`}
                                download={article.file.replace(/-/g, '_')}
                                style={{ marginTop: 0, flexShrink: 0 }}
                            >
                                {isFr ? 'Télécharger' : 'Download'}
                            </DownloadBtn>
                        </div>
                    </div>
                ))}
            </div>
            <PressDocRow>
                <div className="doc-icon">&#128240;</div>
                <div className="doc-info">
                    <div className="doc-title">Traits D'CO</div>
                    <div className="doc-format">DOCX</div>
                </div>
                <DownloadBtn
                    href="/assets/fd100-kit/presse/retombees/Traits-DCO.docx"
                    download="Traits_DCO.docx"
                >
                    {isFr ? 'Télécharger' : 'Download'}
                </DownloadBtn>
            </PressDocRow>

            {/* Articles en ligne */}
            <SubsectionTitle>
                {isFr ? 'Articles en ligne' : 'Online articles'}
            </SubsectionTitle>
            {[
                {
                    title: 'Résidences Décoration',
                    article: 'FD100 x RD Résidences Décoration : votez pour votre coup de cœur design',
                    date: isFr ? 'Avril 2026' : 'April 2026',
                    url: 'https://residences-decoration.com/fd100-x-rd-residences-decoration-votez-pour-votre-coup-de-coeur-design/',
                },
                {
                    title: 'Maison à Part',
                    article: 'FD100 : 100 projets primés en architecture intérieure et design',
                    date: isFr ? '13 avril 2026' : 'April 13, 2026',
                    url: 'https://www.maisonapart.com/edito/s-inspirer/ambiances-et-styles/fd100---100-projets-prims-en-architecture-intrie-14353.php',
                },
                {
                    title: 'Formae Magazine',
                    article: 'French Design Art Edition à Art Paris',
                    date: isFr ? 'Avril 2026' : 'April 2026',
                    url: 'https://formaemagazine.com/fr/articles/french-design-art-edition-a-art-paris',
                },
                {
                    title: 'Say Who',
                    article: 'Art Paris fait la part belle au design avec les FD100 Awards',
                    date: isFr ? 'Avril 2026' : 'April 2026',
                    url: 'https://saywho.fr/evenements/art-paris-fait-la-part-belle-au-design-avec-les-fd100-awards/',
                },
                {
                    title: 'Asakan Art',
                    article: isFr
                        ? 'Quelque 165 galeries et institutions exposent à Art Paris 2026'
                        : '165 galleries and institutions exhibit at Art Paris 2026',
                    date: isFr ? '10 avril 2026' : 'April 10, 2026',
                    url: 'https://asakan.art/france-quelque-165-galeries-et-institutions-exposent-jusquau-12-avril-a-art-paris-2026/',
                },
                {
                    title: 'Résidences Décoration',
                    article: 'Jean-Paul Bath « C\'est parce qu\'il y a un passé qu\'on peut se dire qu\'il y a un futur pour le design »',
                    date: isFr ? '19 mars 2026' : 'March 19, 2026',
                    url: 'https://residences-decoration.com/jean-paul-bath-cest-parce-quil-y-a-un-passe-quon-peut-se-dire-quil-y-a-un-futur-pour-le-design/',
                },
            ].map((article, i) => (
                <PressDocRow key={i}>
                    <div className="doc-icon">&#128279;</div>
                    <div className="doc-info">
                        <div className="doc-title">{article.title}</div>
                        <div className="doc-format" style={{ fontStyle: 'italic' }}>{article.article}</div>
                        <div className="doc-format">{article.date}</div>
                    </div>
                    <DownloadBtn
                        href={article.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ background: 'linear-gradient(135deg, rgb(24, 51, 189), #1a237e)' }}
                    >
                        {isFr ? 'Lire' : 'Read'}
                    </DownloadBtn>
                </PressDocRow>
            ))}
        </>
    );

    // ═════════════════════════════════════════════════════════
    // SECTION 4: Prix du public
    // ═════════════════════════════════════════════════════════
    const renderSection4 = () => {
        return (
            <>
                <SubsectionTitle>
                    {isFr ? 'Présentation' : 'Overview'}
                </SubsectionTitle>
                <StaticTextBlock>
                    {isFr ? (
                        <>
                            <p>Cette année, nous sommes heureux de vous présenter une nouveauté : <strong>le Prix du Public</strong>, issu des 100 projets lauréats du prix Le FD100.</p>
                            <p>Ce prix sera organisé en ligne, accessible à tous, sur nos réseaux sociaux et par la presse en partenariat avec le magazine <strong>Résidences Décoration</strong>.</p>
                            {(isJuryOrOfficiel || isPresse) ? (
                                <p>Les votes se feront via un <strong>lien url</strong> (ci-dessous) qui vous sera transmis prochainement. Celui-ci vous permettra d'accéder à la page de présentation des projets lauréats et de voter pour votre projet préféré, ainsi que de mobiliser votre réseau et d'inviter votre communauté à voter.</p>
                            ) : (
                                <p>Les votes se feront via un <strong>lien url personnalisé</strong> (ci-dessous) qui sera activé prochainement. Celui-ci vous permettra de mobiliser votre réseau et d'inviter votre communauté à voter pour votre projet.</p>
                            )}
                        </>
                    ) : (
                        <>
                            <p>This year, we are pleased to introduce a new feature: <strong>the People's Choice Award</strong>, selected from the 100 winning projects of Le FD100.</p>
                            <p>This online award will be open to the public and promoted on our social media channels and through the press, in partnership with <strong>Résidences Décoration</strong> magazine.</p>
                            {(isJuryOrOfficiel || isPresse) ? (
                                <p>Votes will take place via a URL link, which will give you access to the presentation page of the winning projects, where you will be able to vote for your favorite project and invite your community to vote as well.</p>
                            ) : (
                                <p>Voting will take place via a <strong>personalised URL link</strong> that will be activated shortly.</p>
                            )}
                        </>
                    )}
                </StaticTextBlock>

                {/* Live Score for laureats/partenaires with projects */}
                {!(isJuryOrOfficiel || isPresse) && projects.length > 0 && (
                    <>
                        <SubsectionTitle>
                            {isFr ? 'Score en direct' : 'Live Score'}
                        </SubsectionTitle>
                        {votesLoaded && (
                            <LiveBadge>
                                {isFr ? 'Mis à jour en temps réel' : 'Updated in real-time'}
                            </LiveBadge>
                        )}
                        {votesLoaded && (
                            <TotalStatsRow>
                                <MiniStat>
                                    <MiniStatValue>{totalVotes}</MiniStatValue>
                                    <MiniStatLabel>{isFr ? 'Votes totaux' : 'Total votes'}</MiniStatLabel>
                                </MiniStat>
                                <MiniStat>
                                    <MiniStatValue>{projectsWithVotes}<span style={{ fontSize: '13px', fontWeight: 600, color: '#999' }}>/{TOTAL_FD100_PROJECTS}</span></MiniStatValue>
                                    <MiniStatLabel>{isFr ? 'Projets votés' : 'Projects voted'}</MiniStatLabel>
                                </MiniStat>
                            </TotalStatsRow>
                        )}
                        {votesLoaded && top10Shuffled.length > 0 && Object.keys(top10Data).length > 0 && (
                            <div style={{ marginBottom: '24px' }}>
                                <div style={{ fontSize: '13px', fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' }}>
                                    &#128293; {isFr ? 'Top 10 projets' : 'Top 10 projects'}
                                </div>
                                <div style={{
                                    display: 'flex',
                                    gap: '12px',
                                    overflowX: 'auto',
                                    paddingBottom: '8px',
                                }}>
                                    {top10Shuffled.map((pid) => {
                                        const d = top10Data[pid] || {};
                                        const name = isFr ? (d.name_fr || d.name_en || '') : (d.name_en || d.name_fr || '');
                                        const designer = d.designer || '';
                                        const imageUrl = d.imagePath
                                            ? process.env.REACT_APP_MEDIA_BASE_URL + 'userfiles/' + d.imagePath
                                            : null;
                                        return (
                                            <a
                                                key={pid}
                                                href={`https://connectedby.lefrenchdesign.org/vote/fd100/${pid}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{
                                                    flexShrink: 0,
                                                    width: '160px',
                                                    background: 'white',
                                                    border: '1px solid #e8e8e8',
                                                    borderRadius: '12px',
                                                    overflow: 'hidden',
                                                    textDecoration: 'none',
                                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                                }}
                                                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.1)'; }}
                                                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                                            >
                                                <div style={{
                                                    width: '100%',
                                                    paddingTop: '75%',
                                                    backgroundImage: imageUrl ? `url(${imageUrl})` : 'none',
                                                    backgroundSize: 'cover',
                                                    backgroundPosition: 'center',
                                                    backgroundColor: '#f0f0f0',
                                                }} />
                                                <div style={{ padding: '10px' }}>
                                                    <div style={{
                                                        fontSize: '12px',
                                                        fontWeight: 700,
                                                        color: '#1a1a1a',
                                                        lineHeight: 1.3,
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical',
                                                        overflow: 'hidden',
                                                        textTransform: 'uppercase',
                                                    }}>
                                                        {name}
                                                    </div>
                                                    {designer && (
                                                        <div style={{
                                                            fontSize: '11px',
                                                            color: '#888',
                                                            marginTop: '3px',
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                        }}>
                                                            {designer}
                                                        </div>
                                                    )}
                                                </div>
                                            </a>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                        {projects.map((projectData) => {
                            const pid = projectData.project._id;
                            const myVotes = voteCounts[pid] || 0;
                            const pct = maxVoteCount > 0 ? (myVotes / maxVoteCount) * 100 : 0;
                            const link = `https://connectedby.lefrenchdesign.org/vote/fd100/${pid}`;

                            // Momentum tier based on absolute votes (no rank spoiler)
                            const momentum = myVotes === 0
                                ? { label: isFr ? 'En attente' : 'Awaiting votes', color: '#bdbdbd', bg: '#f5f5f5' }
                                : myVotes < 5
                                ? { label: isFr ? 'Lancé' : 'Getting started', color: '#5c6bc0', bg: '#e8eaf6' }
                                : myVotes < 15
                                ? { label: isFr ? 'En progression' : 'Building momentum', color: 'rgb(24, 51, 189)', bg: '#e3f2fd' }
                                : myVotes < 30
                                ? { label: isFr ? 'Bonne dynamique' : 'Strong traction', color: '#00897b', bg: '#e0f2f1' }
                                : { label: isFr ? 'Engouement fort' : 'High engagement', color: '#e65100', bg: '#fff3e0' };

                            return (
                                <ScoreCardContainer key={pid}>
                                    <ScoreCardTop>
                                        <ScoreMiddle>
                                            <ScoreProjectTitle>{getProjectName(projectData.project)}</ScoreProjectTitle>
                                            <div style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                                background: momentum.bg,
                                                color: momentum.color,
                                                fontSize: '11px',
                                                fontWeight: 700,
                                                padding: '3px 10px',
                                                borderRadius: '20px',
                                                marginBottom: '10px',
                                            }}>
                                                <span style={{
                                                    width: '6px',
                                                    height: '6px',
                                                    borderRadius: '50%',
                                                    background: momentum.color,
                                                }} />
                                                {momentum.label}
                                            </div>
                                            <ScoreProgressTrack>
                                                <ScoreProgressFill style={{ width: `${pct}%` }} />
                                            </ScoreProgressTrack>
                                        </ScoreMiddle>
                                        <ScoreVoteDisplay>
                                            <ScoreVoteNumber>{myVotes}</ScoreVoteNumber>
                                            <ScoreVoteLabel>vote{myVotes !== 1 ? 's' : ''}</ScoreVoteLabel>
                                        </ScoreVoteDisplay>
                                    </ScoreCardTop>
                                    <ScoreCardBottom>
                                        <ShareVoteBtn
                                            href={link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {isFr ? 'Page de vote' : 'Voting page'}
                                        </ShareVoteBtn>
                                        <CopyShareBtn
                                            onClick={() => handleCopyLink(link, `score-${pid}`)}
                                        >
                                            {copiedLink === `score-${pid}`
                                                ? (isFr ? 'Copié !' : 'Copied!')
                                                : (isFr ? 'Copier le lien' : 'Copy link')}
                                        </CopyShareBtn>
                                        <span style={{ fontSize: '12px', color: '#bbb', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                            {link}
                                        </span>
                                    </ScoreCardBottom>
                                </ScoreCardContainer>
                            );
                        })}
                    </>
                )}

                {/* Simple voting link for jury/officiel/presse */}
                {(isJuryOrOfficiel || isPresse) && (
                    <>
                        <SubsectionTitle>
                            {isFr ? 'Lien de vote' : 'Voting Link'}
                        </SubsectionTitle>
                        <DateGatedContent gateDate={DATE_GATES.PERSONALIZED_LINKS}>
                            <PersonalizedLinkRow>
                                <span className="project-label">{isFr ? 'Page de vote :' : 'Voting page:'}</span>
                                <CopyableLink
                                    onClick={() => handleCopyLink('https://connectedby.lefrenchdesign.org/vote/fd100', 'voting-page')}
                                    title={isFr ? 'Cliquer pour copier' : 'Click to copy'}
                                >
                                    {copiedLink === 'voting-page'
                                        ? (isFr ? 'Copié !' : 'Copied!')
                                        : 'https://connectedby.lefrenchdesign.org/vote/fd100'}
                                </CopyableLink>
                            </PersonalizedLinkRow>
                        </DateGatedContent>
                    </>
                )}

                {/* No projects fallback */}
                {!(isJuryOrOfficiel || isPresse) && projects.length === 0 && (
                    <>
                        <SubsectionTitle>
                            {isFr ? 'Lien(s) URL personnalisé(s)' : 'Personalised Voting Link'}
                        </SubsectionTitle>
                        <DateGatedContent gateDate={DATE_GATES.PERSONALIZED_LINKS}>
                            <StaticTextBlock>
                                {isFr ? 'Aucun projet lié.' : 'No linked projects.'}
                            </StaticTextBlock>
                        </DateGatedContent>
                    </>
                )}

                {/* Calendrier */}
                <SubsectionTitle>
                    {isFr ? 'Calendrier de communication' : 'Communication calendar'}
                </SubsectionTitle>
                <TimelineList>
                    {!isJuryOrOfficiel && !isPresse && <li><strong>1 {isFr ? 'avril' : 'April'}</strong> : {isFr
                        ? 'Transmission de votre lien URL personnalisé via la plateforme'
                        : 'Personalized URL link sent via the platform'}</li>}
                    <li><strong>10 {isFr ? 'avril' : 'April'}</strong> : {isFr
                        ? 'Ouverture des votes au public'
                        : 'Opening of public voting'}</li>
                    <li><strong>10 {isFr ? 'avril' : 'April'}</strong> : {isFr
                        ? 'Teasing du prix dans le magazine Résidences Décoration'
                        : 'Award teaser published in Résidences Décoration magazine'}</li>
                    <li><strong>18 {isFr ? 'avril' : 'April'}</strong> : {isFr
                        ? 'Communication autour du prix par l\'influenceuse Alichuree (810K abonnés) sur Instagram'
                        : 'Award promotion on Instagram by influencer Alichuree (810K followers)'}</li>
                    <li><strong>30 {isFr ? 'avril' : 'April'}</strong> : {isFr
                        ? 'Clôture des votes du Public'
                        : 'Public voting closes'}</li>
                    <li><strong>{isFr ? 'Début juin' : 'Early June'}</strong> : {isFr
                        ? 'Révélation du Prix du Public accompagnée d\'un portrait dédié dans le magazine Résidences Décoration (date à confirmer)'
                        : 'Announcement of the People\'s Choice Award winner, with a dedicated feature in Résidences Décoration magazine (date TBC)'}</li>
                    <li><strong>18 {isFr ? 'juin' : 'June'}</strong> : {isFr
                        ? 'Soirée en l\'honneur du Prix du Public à la galerie Le FRENCH DESIGN'
                        : 'Evening event in honour of the People\'s Choice Award at the Le FRENCH DESIGN Gallery'}</li>
                </TimelineList>
            </>
        );
    };

    // ═════════════════════════════════════════════════════════
    // SECTION 5: Exposition – Recensement des pièces (fabricants only)
    // ═════════════════════════════════════════════════════════
    // Only espace-type projects are eligible for the exposition
    const expoProjects = useMemo(() =>
        projects.filter(pd => pd.project.type === 'espace'),
    [projects]);

    const renderSection5 = () => {
        if (expoLoading) return <Spinner />;

        const renderPieceForm = (projectId) => (
            <FormOverlay>
                <FormTitle>
                    {expoEditId
                        ? (isFr ? 'Modifier la pièce' : 'Edit piece')
                        : (isFr ? 'Ajouter une pièce' : 'Add a piece')}
                </FormTitle>
                <FormRow>
                    <FormGroup style={{ flex: '1 1 100%' }}>
                        <label>{isFr ? 'Nom de la pièce' : 'Piece name'}</label>
                        <input
                            value={expoForm.nom_piece}
                            onChange={e => setExpoForm(f => ({ ...f, nom_piece: e.target.value }))}
                            placeholder={isFr ? 'Ex: Chaise Longue LC4' : 'Ex: LC4 Chaise Longue'}
                        />
                    </FormGroup>
                </FormRow>
                <FormRow>
                    <FormGroup>
                        <label>{isFr ? 'Typologie du mobilier' : 'Furniture typology'}</label>
                        <input
                            value={expoForm.typologie}
                            onChange={e => setExpoForm(f => ({ ...f, typologie: e.target.value }))}
                            placeholder={isFr ? 'Ex: Chaise, Table, Luminaire...' : 'Ex: Chair, Table, Lamp...'}
                        />
                    </FormGroup>
                    <FormGroup>
                        <label>{isFr ? 'Date de création' : 'Creation date'}</label>
                        <input
                            value={expoForm.date_creation}
                            onChange={e => setExpoForm(f => ({ ...f, date_creation: e.target.value }))}
                            placeholder={isFr ? 'Ex: 2024' : 'Ex: 2024'}
                        />
                    </FormGroup>
                </FormRow>
                <FormRow>
                    <FormGroup>
                        <label>{isFr ? 'Nom(s) du ou des designer(s)' : 'Designer name(s)'}</label>
                        <input
                            value={expoForm.designer_names}
                            onChange={e => setExpoForm(f => ({ ...f, designer_names: e.target.value }))}
                        />
                    </FormGroup>
                    <FormGroup>
                        <label>{isFabricant
                            ? (isFr ? "Nom de l'éditeur" : 'Publisher name')
                            : (isFr ? 'Nom du fabricant' : 'Manufacturer name')}</label>
                        <input
                            value={expoForm.editeur}
                            onChange={e => setExpoForm(f => ({ ...f, editeur: e.target.value }))}
                        />
                    </FormGroup>
                </FormRow>
                <FormRow>
                    <FormGroup>
                        <label>{isFr ? 'Matériaux' : 'Materials'}</label>
                        <input
                            value={expoForm.materiaux}
                            onChange={e => setExpoForm(f => ({ ...f, materiaux: e.target.value }))}
                            placeholder={isFr ? 'Ex: Bois, Métal, Tissu...' : 'Ex: Wood, Metal, Fabric...'}
                        />
                    </FormGroup>
                    <FormGroup>
                        <label>{isFr ? 'Dimensions' : 'Dimensions'}</label>
                        <input
                            value={expoForm.dimensions}
                            onChange={e => setExpoForm(f => ({ ...f, dimensions: e.target.value }))}
                            placeholder={isFr ? 'Ex: L120 x P60 x H75 cm' : 'Ex: W120 x D60 x H75 cm'}
                        />
                    </FormGroup>
                </FormRow>

                {/* Photo upload */}
                <FormGroup style={{ marginBottom: 0 }}>
                    <label>{isFr ? 'Visuels (photos haute résolution)' : 'Visuals (high resolution photos)'}</label>
                </FormGroup>
                <PhotoDropZone
                    isDragOver={expoDragOver}
                    onDragOver={e => { e.preventDefault(); setExpoDragOver(true); }}
                    onDragLeave={() => setExpoDragOver(false)}
                    onDrop={handleExpoPhotoDrop}
                    onClick={() => expoFileInputRef.current?.click()}
                >
                    <input
                        ref={expoFileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        multiple
                        style={{ display: 'none' }}
                        onChange={handleExpoPhotoDrop}
                    />
                    {isFr
                        ? 'Glissez vos photos ici ou cliquez pour sélectionner'
                        : 'Drag photos here or click to select'}
                    <br />
                    <span style={{ fontSize: 12, color: '#aaa' }}>JPEG, PNG, WebP — max 20 MB</span>
                </PhotoDropZone>

                {/* Existing photos (edit mode) */}
                {expoExistingPhotos.length > 0 && (
                    <PhotoPreviewGrid>
                        {expoExistingPhotos.map((url, i) => (
                            <PhotoPreviewItem key={`existing-${i}`}>
                                <img src={url} alt="" />
                                <button className="remove-btn" onClick={() => removeExpoExistingPhoto(url)}>&times;</button>
                            </PhotoPreviewItem>
                        ))}
                    </PhotoPreviewGrid>
                )}

                {/* New photos preview */}
                {expoPhotos.length > 0 && (
                    <PhotoPreviewGrid>
                        {expoPhotos.map((file, i) => (
                            <PhotoPreviewItem key={`new-${i}`}>
                                <img src={URL.createObjectURL(file)} alt="" />
                                <button className="remove-btn" onClick={() => removeExpoNewPhoto(i)}>&times;</button>
                            </PhotoPreviewItem>
                        ))}
                    </PhotoPreviewGrid>
                )}

                <FormBtnRow>
                    <SubmitBtn
                        onClick={() => handleExpoSubmit(projectId)}
                        disabled={expoSubmitting}
                    >
                        {expoSubmitting
                            ? (isFr ? 'Envoi en cours...' : 'Submitting...')
                            : expoEditId
                                ? (isFr ? 'Enregistrer' : 'Save')
                                : (isFr ? 'Ajouter' : 'Add')}
                    </SubmitBtn>
                    <CancelBtn onClick={resetExpoForm}>
                        {isFr ? 'Annuler' : 'Cancel'}
                    </CancelBtn>
                </FormBtnRow>
            </FormOverlay>
        );

        return (
            <>
                {expoProjects.map((projectData) => {
                    const pid = projectData.project._id;
                    const projectName = getProjectName(projectData.project);
                    const projectPieces = expoPieces.filter(p => p.project_id === pid);

                    return (
                        <ExpoProjectBlock key={pid}>
                            <ExpoProjectTitle>{projectName}</ExpoProjectTitle>

                            {/* Existing pieces */}
                            {projectPieces.map(piece => (
                                <PieceCard key={piece.id}>
                                    {piece.photo_urls?.[0] && (
                                        <PieceThumb src={piece.photo_urls[0]} alt="" />
                                    )}
                                    <PieceInfo>
                                        <div className="piece-title">
                                            {piece.nom_piece || piece.typologie || (isFr ? 'Pièce sans nom' : 'Unnamed piece')}
                                        </div>
                                        {piece.designer_names && <div className="piece-detail">{isFr ? 'Designer(s)' : 'Designer(s)'}: {piece.designer_names}</div>}
                                        {piece.editeur && <div className="piece-detail">{isFabricant ? (isFr ? 'Éditeur' : 'Publisher') : (isFr ? 'Fabricant' : 'Manufacturer')}: {piece.editeur}</div>}
                                        {piece.materiaux && <div className="piece-detail">{isFr ? 'Matériaux' : 'Materials'}: {piece.materiaux}</div>}
                                        {piece.dimensions && <div className="piece-detail">Dimensions: {piece.dimensions}</div>}
                                        {piece.date_creation && <div className="piece-detail">{isFr ? 'Création' : 'Created'}: {piece.date_creation}</div>}
                                        {piece.photo_urls?.length > 1 && (
                                            <div className="piece-detail" style={{ color: 'rgb(24, 51, 189)' }}>
                                                {piece.photo_urls.length} {isFr ? 'photos' : 'photos'}
                                            </div>
                                        )}
                                    </PieceInfo>
                                    <PieceActions>
                                        <SmallBtn onClick={() => openExpoEdit(piece)}>
                                            {isFr ? 'Modifier' : 'Edit'}
                                        </SmallBtn>
                                        <SmallBtn danger onClick={() => handleExpoDelete(piece.id)}>
                                            {isFr ? 'Supprimer' : 'Delete'}
                                        </SmallBtn>
                                    </PieceActions>
                                </PieceCard>
                            ))}

                            {/* Form */}
                            {expoFormOpen === pid
                                ? renderPieceForm(pid)
                                : (
                                    <AddPieceBtn onClick={() => openExpoAdd(pid)}>
                                        + {isFr ? 'Ajouter une pièce' : 'Add a piece'}
                                    </AddPieceBtn>
                                )}
                        </ExpoProjectBlock>
                    );
                })}
            </>
        );
    };

    // ═════════════════════════════════════════════════════════
    // RENDER
    // ═════════════════════════════════════════════════════════
    return (
        <Section className="padding">
            <Container>
                {/* Confidential banner removed - past embargo date */}
                <KitHeader>
                    {displayLogo && <LogoImg src={displayLogo} alt={displayName} />}
                    <H0>Kit {isPresse
                        ? (isFr ? 'Presse' : 'Press')
                        : mode === 'partenaire'
                            ? (isFr ? 'Partenaire' : 'Partner')
                            : (isFr ? 'Lauréat' : 'Winner')}</H0>
                    <Subtitle>{displayName}</Subtitle>
                    {!isPresse && mode === 'partenaire' && partenaireType && (
                        <PartenaireBadge type={partenaireType}>
                            {{
                                fabricant: isFr ? 'Fabricant' : 'Manufacturer',
                                ecole: isFr ? 'École' : 'School',
                                distributeur: isFr ? 'Distributeur' : 'Distributor',
                                editeur: isFr ? 'Galerie / Éditeur' : 'Gallery / Editor',
                                lieu: isFr ? 'Lieu' : 'Venue',
                                institution: isFr ? 'Partenaire Jury' : 'Jury Partner',
                                officiel: isFr ? 'Partenaire Officiel' : 'Official Partner',
                            }[partenaireType] || partenaireType}
                        </PartenaireBadge>
                    )}
                </KitHeader>

                {/* Exposition highlight - fabricants & éditeurs with pieces=true */}
                {isExpoEligible && expoProjects.length > 0 && (
                    <ExpoHighlight>
                        <div className="expo-badge">
                            &#9733; {isFr ? 'Action requise' : 'Action required'}
                        </div>
                        <div className="expo-title">
                            {isFr
                                ? 'Exposition octobre – Recensement des pièces lauréates'
                                : 'October Exhibition – Winning Pieces Census'}
                        </div>
                        <div className="expo-desc">
                            {isFr
                                ? 'Dans le cadre de l\'exposition prévue début octobre à la galerie Le FRENCH DESIGN, merci de renseigner ci-dessous les informations relatives aux pièces de mobilier présentées dans vos projets lauréats (espaces).'
                                : 'As part of the exhibition planned for early October at the Le FRENCH DESIGN gallery, please provide below the information about the furniture pieces featured in your winning space projects.'}
                        </div>

                        {/* Empty state warning */}
                        {expoPieces.length === 0 && !expoLoading && (
                            <div style={{
                                background: '#fff3e0',
                                border: '2px solid #ff9800',
                                borderRadius: '10px',
                                padding: '20px 24px',
                                marginBottom: '20px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '14px',
                            }}>
                                <span style={{ fontSize: '28px' }}>&#9888;</span>
                                <div>
                                    <div style={{ fontWeight: 700, fontSize: '15px', color: '#e65100', marginBottom: '4px' }}>
                                        {isFr ? 'Aucune pièce soumise' : 'No pieces submitted yet'}
                                    </div>
                                    <div style={{ fontSize: '14px', color: '#bf360c' }}>
                                        {isFr
                                            ? 'Veuillez ajouter au moins une pièce ci-dessous pour chaque projet avant le mercredi 29 avril.'
                                            : 'Please add at least one piece below for each project before Wednesday April 29.'}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Pieces section — PRIMARY ACTION */}
                        {renderSection5()}

                        {/* Contact section — collapsible, secondary */}
                        <div style={{
                            marginTop: '24px',
                            borderTop: '1px solid #e0e4f0',
                            paddingTop: '16px',
                        }}>
                            <div
                                onClick={() => setExpoContactOpen(prev => !prev)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    cursor: 'pointer',
                                    userSelect: 'none',
                                    padding: '8px 0',
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <span style={{ fontSize: '15px', fontWeight: 600, color: '#555' }}>
                                        {isFr ? 'Informations de contact' : 'Contact information'}
                                    </span>
                                    {expoContact.contact_prenom && expoContact.contact_nom && (
                                        <span style={{
                                            fontSize: '12px',
                                            color: '#2e7d32',
                                            background: '#e8f5e9',
                                            padding: '2px 10px',
                                            borderRadius: '12px',
                                            fontWeight: 600,
                                        }}>
                                            {expoContact.contact_prenom} {expoContact.contact_nom}
                                        </span>
                                    )}
                                </div>
                                <span style={{
                                    fontSize: '18px',
                                    transition: 'transform 0.3s',
                                    transform: expoContactOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                    color: '#999',
                                }}>&#9660;</span>
                            </div>
                            {expoContactOpen && (
                                <ExpoProjectBlock style={{ marginTop: '12px' }}>
                                    <FormRow>
                                        <FormGroup>
                                            <label>{isFr ? 'Prénom' : 'First name'}</label>
                                            <input
                                                value={expoContact.contact_prenom}
                                                onChange={e => setExpoContact(c => ({ ...c, contact_prenom: e.target.value }))}
                                            />
                                        </FormGroup>
                                        <FormGroup>
                                            <label>{isFr ? 'Nom' : 'Last name'}</label>
                                            <input
                                                value={expoContact.contact_nom}
                                                onChange={e => setExpoContact(c => ({ ...c, contact_nom: e.target.value }))}
                                            />
                                        </FormGroup>
                                    </FormRow>
                                    <FormRow>
                                        <FormGroup>
                                            <label>Email</label>
                                            <input
                                                type="email"
                                                value={expoContact.contact_email}
                                                onChange={e => setExpoContact(c => ({ ...c, contact_email: e.target.value }))}
                                            />
                                        </FormGroup>
                                        <FormGroup>
                                            <label>{isFr ? 'Téléphone' : 'Phone'}</label>
                                            <input
                                                type="tel"
                                                value={expoContact.contact_telephone}
                                                onChange={e => setExpoContact(c => ({ ...c, contact_telephone: e.target.value }))}
                                            />
                                        </FormGroup>
                                    </FormRow>
                                    <FormBtnRow>
                                        <SubmitBtn onClick={handleExpoContactSave} disabled={expoContactSaving}>
                                            {expoContactSaving
                                                ? (isFr ? 'Enregistrement...' : 'Saving...')
                                                : expoContactSaved
                                                    ? (isFr ? 'Enregistré !' : 'Saved!')
                                                    : (isFr ? 'Enregistrer le contact' : 'Save contact')}
                                        </SubmitBtn>
                                    </FormBtnRow>
                                </ExpoProjectBlock>
                            )}
                        </div>
                    </ExpoHighlight>
                )}

                {/* Projects summary row */}
                {!isPresse && projects.length > 0 && (
                    <ProjectsRow>
                        {projects.map((projectData, idx) => mode === 'partenaire' ? (
                            <PartenaireProjectCard key={idx}>
                                <ProjectTitle>
                                    {getProjectName(projectData.project)}
                                </ProjectTitle>
                                <PartenaireProjectImage
                                    src={GetProjectMainImage(projectData.project) || ''}
                                />
                                <ProjectInfo>
                                    <div style={{color: 'black', fontWeight: 'bold', fontSize: '0.9rem', marginBottom: '8px'}}>
                                        {getDesignerName(projectData.project, projectData.user)}
                                    </div>
                                    <div style={{color: 'black', fontWeight: 'bold', fontSize: '1rem'}}>
                                        {projectData.project.type === 'objet'
                                            ? (isFr ? 'Objet' : 'Object')
                                            : (isFr ? 'Espace' : 'Space')}
                                    </div>
                                    {(getDefinitiveTypology(projectData.project) || projectData.project.typologie) &&
                                        <div className='minilabel'>
                                            {isFr ? 'Typologie: ' : 'Typology: '}
                                            <b style={{color: 'black', fontWeight: 'bold'}}>{getDefinitiveTypology(projectData.project)}</b>
                                        </div>
                                    }
                                </ProjectInfo>
                            </PartenaireProjectCard>
                        ) : (
                            <LaureatProjectCard key={idx} onClick={() => onProjectClick && onProjectClick(projectData)}>
                                <ProjectTitle>{getProjectName(projectData.project)}</ProjectTitle>
                                <LaureatProjectImage
                                    src={GetProjectMainImage(projectData.project) || ''}
                                />
                                <ProjectInfo>
                                    <div style={{color: 'black', fontWeight: 'bold', fontSize: '1rem'}}>
                                        {projectData.project.type === 'objet'
                                            ? (isFr ? 'Objet' : 'Object')
                                            : (isFr ? 'Espace' : 'Space')}
                                    </div>
                                    {projectData.project.typologie &&
                                        <div className='minilabel'>
                                            {isFr ? 'Typologie: ' : 'Typology: '}
                                            <b style={{color: 'black', fontWeight: 'bold'}}>{projectData.project.typologie}</b>
                                        </div>
                                    }
                                    {projectData.project.year &&
                                        <div className='minilabel'>
                                            {isFr ? 'Année: ' : 'Year: '}
                                            <b style={{color: 'black', fontWeight: 'bold'}}>{projectData.project.year}</b>
                                        </div>
                                    }
                                </ProjectInfo>
                            </LaureatProjectCard>
                        ))}
                    </ProjectsRow>
                )}

                {/* 4 accordion sections */}
                {renderAccordion(
                    1,
                    isFr ? 'Photos de la cérémonie de révélation le 9 avril' : 'Awards ceremony photos - April 9th',
                    renderSection1()
                )}
                {renderAccordion(
                    2,
                    isFr ? 'Éléments de communication & Charte graphique' : 'Communication materials & graphic guidelines',
                    renderSection2()
                )}
                {renderAccordion(
                    3,
                    isFr ? 'Presse' : 'Press materials',
                    renderSection3()
                )}
                {renderAccordion(
                    4,
                    <>{isFr ? 'Prix du public' : 'People\'s Choice Award'} <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '5px',
                        background: '#e8f5e9',
                        color: '#2e7d32',
                        fontSize: '11px',
                        fontWeight: 700,
                        padding: '3px 10px',
                        borderRadius: '20px',
                        marginLeft: '10px',
                        verticalAlign: 'middle',
                    }}><span style={{
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        background: '#2e7d32',
                        animation: 'scoreLivePulse 2s ease-in-out infinite',
                    }} />{isFr ? 'En cours' : 'Live'}</span></>,
                    renderSection4()
                )}
            </Container>
        </Section>
    );
};

export default FD100KitPage;
